import React from 'react';
import {MUIBox, MUITypography} from '@/components/MUI-Components';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {colors, fontFamily, fontSize, borderRadius} from '@/styles/tokens';

// Styling migrated from _blog-detail-new.scss (.bdn-takeaways-*) into module-level
// sx constants driven by tokens.ts. This card renders INSIDE .bdn-blog-content,
// which styles bare ul/li/p (and Typography's <p>) with !important — so the ul/li/p
// and title/text declarations keep !important (exactly as the SCSS did) to win that
// cascade. Layout-only props (flex/gap/radius) need no !important.
const sxCard = {
    background: colors.background.tag,
    border: `1px solid ${colors.accent.blueBorder}`,
    borderRadius: borderRadius.lg,
    p: 3,
    mb: 4
} as const;

const sxHeader = {display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px !important'} as const;

const sxIcon = {
    width: '36px !important',
    height: '36px !important',
    background: colors.primary.navy,
    borderRadius: borderRadius.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {fontSize: `${fontSize[18]} !important`, color: `${colors.background.default} !important`}
} as const;

const sxTitle = {
    fontFamily: `${fontFamily.heading} !important`,
    fontSize: `${fontSize[18]} !important`,
    color: `${colors.text.primary} !important`
} as const;

const sxList = {
    listStyle: 'none !important',
    padding: '0 !important',
    margin: '0 !important',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
} as const;

const sxItem = {display: 'flex', alignItems: 'flex-start', gap: '6px'} as const;

const sxCheck = {
    fontSize: `${fontSize[18]} !important`,
    color: `${colors.primary.main} !important`,
    flexShrink: 0,
    marginTop: '3px',
    display: 'block'
} as const;

const sxText = {
    fontFamily: `${fontFamily.body} !important`,
    fontSize: `${fontSize[16]} !important`,
    lineHeight: '1.65 !important',
    color: `${colors.text.primary} !important`,
    margin: '0 !important',
    padding: '0 !important'
} as const;

// "Key Takeaways" card. Render guard preserved (returns null) → identical output.
const BlogKeyTakeaways = ({keyTakeaways}: {keyTakeaways: string[]}) => {
    if (!(keyTakeaways.length > 0)) return null;
    return (
        <MUIBox sx={sxCard}>
            <MUIBox sx={sxHeader}>
                <MUIBox sx={sxIcon}>
                    <AutoAwesomeIcon />
                </MUIBox>
                <MUITypography sx={sxTitle}>Key Takeaways</MUITypography>
            </MUIBox>
            <MUIBox component="ul" sx={sxList}>
                {keyTakeaways.map((point: string, i: number) => (
                    <MUIBox component="li" key={i} sx={sxItem}>
                        <CheckCircleOutlineIcon sx={sxCheck} />
                        <MUIBox component="p" sx={sxText}>
                            {point}
                        </MUIBox>
                    </MUIBox>
                ))}
            </MUIBox>
        </MUIBox>
    );
};

export default BlogKeyTakeaways;
